// PostHog browser client init. This file is the *only* place the SDK is
// initialized — no `PostHogProvider` component, no React-tree wiring (per
// PostHog's documented Next.js 15.3+ App Router pattern). Feature code
// imports `posthog` from `posthog-js` directly.
//
// Privacy posture:
// - `cookieless_mode: 'on_reject'` is the core of the design. While the SDK is
//   opted *out* (no consent decision yet, or analytics denied) it still
//   captures — but stores NOTHING on the device: no cookie, no localStorage,
//   no sessionStorage. Identity is a privacy-preserving hash computed on
//   PostHog's servers. ePrivacy Art. 5(3) only bites when you store or read
//   information on the device, so a cookieless baseline needs no consent,
//   which is exactly how Vercel Analytics can run bannerless. On opt-in the
//   SDK switches to normal cookie-backed identity.
//   REQUIRES "Cookieless server hash mode" to be enabled in PostHog project
//   settings → Web analytics. Without it PostHog silently DROPS every
//   cookieless event.
// - `opt_out_capturing_by_default: true` therefore no longer means "silent".
//   In cookieless mode it means "capture, but without device storage". It
//   stays set so a first-time visitor can never get a cookie before deciding.
//   Note posthog-js#2841: on older versions this combination captured nothing
//   until `opt_out_capturing()` was called explicitly. `ConsentBanner` makes
//   that call on mount for the no-decision case, so the behaviour is pinned
//   regardless of which side of the fix the installed version falls on.
// - `persistence` is consent-aware at init. A visitor with a *stored*
//   analytics grant (read synchronously from `marka.consent.v2` before
//   `posthog.init`) boots straight into 'localStorage+cookie' so the SDK
//   reads back the identity a previous grant persisted. Without this, every
//   hard navigation (e.g. the cart link's deliberate plain `<a href>`) booted
//   a fresh in-memory id, and `ConsentBanner`'s mount-time re-apply then
//   migrated that fresh id over the stored one — minting a new person and
//   session per page load and orphaning the pre-navigation journey.
//   Everyone else (no decision yet, or denied) starts on 'memory' as
//   belt-and-braces on top of cookieless mode: even if the cookieless hash
//   path were misconfigured, no id reaches the device. The consent bridge
//   (`src/integrations/posthog/consent.ts`) still flips persistence for
//   in-session grant/revoke; switching the type back to 'memory' on revoke
//   also clears anything a prior grant wrote.
// - `disable_session_recording: true` is REQUIRED now that capture is live
//   pre-consent. Replay is a categorically heavier form of processing than a
//   pageview count, so it stays strictly consent-gated: the bridge calls
//   `startSessionRecording()` only on grant. Without this flag the remote
//   config would start recording as soon as capture went live.
// - `advanced_disable_feature_flags: true` skips PostHog feature-flag
//   evaluation. Märka uses the in-house `src/lib/feature-flags.ts`
//   (DEC-0028) so we don't need flag results. We deliberately do *not*
//   set `advanced_disable_flags: true` here, because that knob also
//   short-circuits the `/array/<token>/config` remote-config fetch that
//   session replay (task 137) needs in order to start — without the
//   remote config, posthog-js logs "persisted remote config is stale,
//   requesting fresh config before starting" and never starts recording.
//   `advanced_disable_feature_flags` is the finer-grained switch:
//   recording config still loads, flag evaluation is skipped.
// - `respect_dnt: true` honors the browser Do-Not-Track signal.
// - `person_profiles: 'identified_only'` keeps anonymous traffic anonymous
//   and roughly 4× cheaper than identified events. Märka users are
//   identified only on the post-checkout return (task 140).
// - Session replay config below is ON with conservative defaults (task 137)
//   but gated shut at init by `disable_session_recording` — see above. The
//   masking rules apply once the bridge starts it: all inputs
//   masked, anything matching `[data-ph-no-capture], .ph-no-capture` is
//   blocked entirely (configurator name input, the live preview, the
//   cart's rendered name), anything matching `[data-pii], .ph-mask` has
//   its text masked. Console-log capture is pinned `false` so future
//   default flips don't start hoovering up customer-typed names from
//   stray dev logs. Policy doc: `docs/observability.md`.
//
// Autocapture posture (`defaults: '2026-01-30'` keeps it ON):
// PostHog auto-records click targets and form-input metadata (no input
// values). We rely on `data-ph-no-capture` markers (task 137) to suppress
// the few sensitive elements (cart "Remove [name]" buttons, configurator
// name input, post-checkout email). If event volume ever bumps the billing
// cap, flip to `autocapture: false` and lean on the typed `track()` helper.
//
// Token guard: the bare `posthog.init(...!)` would throw on a missing env.
// In environments without the token (preview deploys without the var,
// SSR-only contexts) we silently skip init. The server-side
// `POSTHOG_ANALYTICS_MODE` kill switch (task 130) gates whether the token
// is even present in a given Vercel scope.

import posthog from "posthog-js";

import { getConsent } from "@/features/consent/consent-storage";
import { beforeSendScrubExceptions } from "@/integrations/posthog/before-send";
import { snapshotLandingUtm } from "@/integrations/posthog/landing-attribution";

// FIRST, and outside every gate below. The landing URL's `utm_*` parameters are
// only readable while that URL is still the current one: a client-side
// navigation takes the query string with it, and the SDK's own `$initial_utm_*`
// are not written until consent makes its store durable — by which time the
// parameters are gone (order #1046 lost its keyword exactly this way; see
// `landing-attribution.ts`). This is a read into memory, so it stores nothing
// on the device and needs no consent, and it is kept clear of the localhost and
// token guards so the snapshot never depends on whether the SDK inits.
snapshotLandingUtm();

/**
 * Local-host gate: development traffic must never reach the production
 * PostHog project. Measured 2026-08-12: 96 localhost sessions in 30 days —
 * ~40% of all "Desktop" sessions — polluting the Sales & Attribution
 * dashboard and every device-split insight.
 *
 * Hostname-based rather than NODE_ENV so a local `next build && next start`
 * (production NODE_ENV, still the operator's machine) is excluded too.
 * `.localhost` subdomains are in the list because the company-host routing
 * work uses them in dev.
 *
 * Escape hatch: `NEXT_PUBLIC_POSTHOG_DEBUG=1` (the existing SDK-debug flag)
 * re-enables local capture for deliberately smoke-testing the PostHog
 * integration itself.
 */
function isLocalHostname(hostname: string): boolean {
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "::1" ||
    hostname === "[::1]" ||
    hostname.endsWith(".localhost")
  );
}

const captureAllowed =
  typeof window !== "undefined" &&
  (!isLocalHostname(window.location.hostname) ||
    process.env.NEXT_PUBLIC_POSTHOG_DEBUG === "1");

if (captureAllowed && process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN) {
  // Stored consent must be read BEFORE init so a returning grantee boots with
  // cookie-backed persistence and keeps their identity across hard
  // navigations. `consent-storage` is dependency-free and window-guarded, so
  // it is safe in this pre-React context. If localStorage is unreadable this
  // returns null and we fall back to the memory/cookieless baseline.
  const analyticsGranted = getConsent()?.analytics === "granted";

  posthog.init(process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN, {
    api_host: "/ingest",
    ui_host: "https://eu.posthog.com",
    defaults: "2026-01-30",
    person_profiles: "identified_only",
    capture_pageleave: true,
    capture_exceptions: true,
    enable_recording_console_log: false,
    disable_session_recording: true,
    session_recording: {
      maskAllInputs: true,
      maskInputOptions: {
        password: true,
        email: true,
        tel: true,
        number: true,
      },
      maskTextSelector: "[data-pii], .ph-mask",
      blockSelector: "[data-ph-no-capture], .ph-no-capture",
    },
    // Logs (task: PostHog Logs over OTel — client half). `captureConsoleLogs`
    // is pinned off for the same reason as `enable_recording_console_log`:
    // stray dev logs in the configurator carry customer-typed names, and the
    // logs path has NO before_send hook — deliberate `logClient()` call sites
    // only. CAUTION: the shipped remote-config handler only ever flips this
    // to `true` (`if (captureConsoleLogs) enable()`), so `false` here does
    // NOT protect against a project-settings flip — keep it off in PostHog
    // project settings too. Same trap as `disable_session_recording` above.
    logs: {
      captureConsoleLogs: false,
      // Sibling of MARKA_SERVER_SERVICE_NAME ("marka-server") — makes browser
      // records filterable next to server ones in the Logs service dropdown.
      serviceName: "marka-web",
    },
    respect_dnt: true,
    cookieless_mode: "on_reject",
    opt_out_capturing_by_default: true,
    // posthog-js records the opt-in/opt-out flag as `__ph_opt_in_out_<token>`
    // and defaults to storing it in a COOKIE. Pin it to localStorage so the
    // cookieless baseline sets no cookie at all. (There is no 'memory' option —
    // passing one silently falls back to the cookie, verified in the browser.)
    // The flag is a single boolean tracking-preference, the same category as
    // our own `marka.consent.v2`, and both are listed as strictly necessary in
    // the privacy policy.
    opt_out_capturing_persistence_type: "localStorage",
    persistence: analyticsGranted ? "localStorage+cookie" : "memory",
    advanced_disable_feature_flags: true,
    before_send: beforeSendScrubExceptions,
    debug: process.env.NEXT_PUBLIC_POSTHOG_DEBUG === "1",
  });

  // Tag every event with the build's release version so PostHog can match
  // incoming exception stacks against the source maps uploaded by
  // scripts/upload-sourcemaps.mjs (task 136). Empty/unset is treated as
  // "no release" by PostHog. The two identifiers MUST match — both come
  // from VERCEL_GIT_COMMIT_SHA on Vercel; locally the upload script falls
  // back to `git rev-parse HEAD`, but the SDK side has no equivalent
  // fallback (the build inlines whatever NEXT_PUBLIC_POSTHOG_RELEASE was
  // at build time), so a local `pnpm build` without the env set ships
  // events with no release tag.
  if (process.env.NEXT_PUBLIC_POSTHOG_RELEASE) {
    posthog.register({
      $release_version: process.env.NEXT_PUBLIC_POSTHOG_RELEASE,
    });
  }

  // Expose the SDK on `window.posthog` in dev so DevTools console can call
  // `posthog.has_opted_in_capturing()` / `posthog.opt_in_capturing()` while
  // smoke-testing the consent flow. Production stays clean — no global leak,
  // and feature code imports `posthog-js` directly anyway.
  if (process.env.NODE_ENV === "development") {
    (window as unknown as { posthog: typeof posthog }).posthog = posthog;
  }
}
